import React from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Check, Image as ImageIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';

const PageBuilderManager = ({ 
  pages, 
  selectedPage, 
  setSelectedPage, 
  currentPage,
  branding,
  activeTheme,
  selectedFeaturedProducts,
  featuredProductsData,
  onOpenProductSelector
}) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left: Page Selection */}
      <div className="lg:col-span-1">
        <Card>
          <CardHeader>
            <CardTitle>Welche Seite möchten Sie bearbeiten?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pages.map((page) => (
              <button
                key={page.id}
                onClick={() => setSelectedPage(page.id)}
                className={`w-full text-left rounded-lg border-2 transition-all overflow-hidden ${
                  selectedPage === page.id
                    ? 'border-[#C4A084] bg-[#C4A084] bg-opacity-10'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {/* Preview Image */}
                <div className="relative h-32 bg-gray-100 overflow-hidden">
                  <div 
                    className="w-full h-full flex flex-col items-center justify-center text-white"
                    style={{ 
                      background: `linear-gradient(135deg, ${activeTheme?.colors?.primary || '#C4A084'} 0%, ${activeTheme?.colors?.secondary || '#B89968'} 100%)`
                    }}
                  >
                    <span className="text-5xl mb-2">{page.icon}</span>
                    <span className="text-sm font-semibold">{branding?.shopName || 'Ihr Shop'}</span>
                  </div>
                  {selectedPage === page.id && (
                    <div className="absolute top-2 right-2 bg-[#C4A084] text-white rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                  )}
                </div>
                
                {/* Text Content */}
                <div className="p-3">
                  <h3 className="font-semibold mb-1 flex items-center gap-2">
                    <span className="text-xl">{page.icon}</span>
                    {page.name}
                  </h3>
                  <p className="text-xs text-gray-600">{page.description}</p>
                </div>
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="text-base">Schnellzugriff</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Link to="/admin/products" className="block">
              <Button variant="outline" className="w-full justify-start" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Neues Produkt hinzufügen
              </Button>
            </Link>
            <Link to="/admin/categories" className="block">
              <Button variant="outline" className="w-full justify-start" size="sm">
                <Edit className="h-4 w-4 mr-2" />
                Kategorien verwalten
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Right: Live Preview & Sections */}
      <div className="lg:col-span-2 space-y-6">
        {/* Live Preview */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl">{currentPage?.name}</CardTitle>
                <p className="text-sm text-gray-600 mt-1">{currentPage?.description}</p>
              </div>
              <a 
                href={selectedPage === 'home' ? '/' : `/${selectedPage}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
              >
                Seite öffnen →
              </a>
            </div>
          </CardHeader>
          <CardContent>
            {/* Embedded Preview */}
            <div className="border-4 border-gray-300 rounded-lg overflow-hidden" style={{ height: '400px' }}>
              <iframe
                src={selectedPage === 'home' ? '/' : `/${selectedPage}`}
                className="w-full h-full"
                title="Seiten-Vorschau"
                style={{ transform: 'scale(0.8)', transformOrigin: 'top left', width: '125%', height: '125%' }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              ↑ Live-Vorschau Ihrer Seite (verkleinert) - Öffnen Sie die Seite in einem neuen Tab für die volle Ansicht
            </p>
          </CardContent>
        </Card>

        {/* Editable Sections */}
        <Card>
          <CardHeader>
            <CardTitle>Was möchten Sie auf dieser Seite ändern?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {currentPage?.sections.map((section) => (
                <div
                  key={section.id}
                  className="border-2 rounded-lg p-4 hover:border-[#C4A084] transition-colors bg-white"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="font-semibold text-base mb-1">{section.name}</h4>
                      <p className="text-sm text-gray-600">{section.description}</p>
                      {section.id === 'featured-products' && (
                        <p className="text-sm text-[#C4A084] font-medium mt-2">
                          ✓ {selectedFeaturedProducts.length} von 12 Produkten ausgewählt
                        </p>
                      )}
                    </div>
                    {section.custom ? (
                      <Button
                        onClick={onOpenProductSelector}
                        size="sm"
                        className="flex-shrink-0 bg-gradient-to-r from-[#C4A084] to-[#B89968] hover:opacity-90 text-white"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Auswählen
                      </Button>
                    ) : section.editable ? (
                      <Link to={section.link}>
                        <Button 
                          size="sm" 
                          className="flex-shrink-0 bg-gradient-to-r from-[#C4A084] to-[#B89968] hover:opacity-90 text-white"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Bearbeiten
                        </Button>
                      </Link>
                    ) : (
                      <span className="text-xs text-gray-500 px-3 py-2 bg-gray-100 rounded">
                        Fest eingebaut
                      </span>
                    )}
                  </div>

                  {/* Show mini preview of featured products */}
                  {section.id === 'featured-products' && featuredProductsData.length > 0 && (
                    <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
                      {featuredProductsData.slice(0, 6).map((product) => (
                        <div key={product.id} className="flex-shrink-0 w-24">
                          {product.images?.[0] ? (
                            <img
                              src={product.images[0]}
                              alt={product.name}
                              className="w-24 h-24 object-cover rounded border-2 border-gray-200"
                            />
                          ) : (
                            <div className="w-24 h-24 bg-gray-200 rounded border-2 border-gray-300 flex items-center justify-center">
                              <ImageIcon className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                          <p className="text-xs mt-1 truncate" title={product.name}>
                            {product.name}
                          </p>
                        </div>
                      ))}
                      {featuredProductsData.length > 6 && (
                        <div className="flex-shrink-0 w-24 h-24 bg-gray-100 rounded border-2 border-gray-300 flex items-center justify-center">
                          <span className="text-sm text-gray-600 font-medium">
                            +{featuredProductsData.length - 6}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PageBuilderManager;
